import React, { useCallback, useContext, useMemo } from 'react';

// DEPENDENCIES
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// GLOBAL VARIABLES
import { INTERFACE, INVENTORY } from 'pathnames.js';

// FUNCTIONS
import { modalFunctions, makePath } from 'functions';

// HELPERS
import { RecordContext } from 'helpers/getRecordData';

// GLOBAL COMPONENTS
import Button from 'core/tools/Button';

// LOCAL COMPONENTS
import StockNumberComponent from '../../tools/StockNumber';

// LOCAL VARIABLES
import { INVENTORY_SOURCE_IDS } from '../variables';

// STYLES
import styles from './stockNumber.module.scss';

// MAIN COMPONENT
const StockNumber = () => {
  // ROUTER
  const navigate = useNavigate();

  // CONTEXT
  const { record } = useContext(RecordContext) || {};

  // REDUX SELECTORS
  const isMobile = useSelector(({ mobile }) => mobile.mobile);

  // CONTEXT
  const { values } = useFormikContext() || {};
  const {
    inventory_source_stock_num, //
    source_name,
    source_sales_id,
    stock_num
  } = useMemo(() => values || {}, [values]);

  const { inventory_source_id } = record || {};
  const isTrade = useMemo(() => parseInt(inventory_source_id) === INVENTORY_SOURCE_IDS.TRADE, [inventory_source_id]);

  const handleEditStockNumber = useCallback(() => {
    const link = !!source_sales_id
      ? makePath(INTERFACE.sales, 'edit', source_sales_id)
      : makePath(INTERFACE.inventory, INVENTORY.inStock);
    modalFunctions.add({
      type: 'confirmation',
      title: 'Edit Stock Number',
      body: (
        <>
          <div className="mb-4">
            <span className="text-danger">Alert:</span> In order to edit the Stock # {stock_num} of this trade in, you
            will be accessing the original deal for ({source_name}, {inventory_source_stock_num})
          </div>
          <div>
            <span className="text-danger">Alert:</span> Please note the following: Changing the Stock# on the original
            deal may require a new deal invoice (re-bill). The new Stock# will need to be updated on Stock stickers, key
            tags, key machine registration, etc. Would you like to continue to edit Stock # {stock_num}?
          </div>
        </>
      ),
      closeButton: false,
      continueButton: {
        label: 'Edit Deal',
        onClick: () => navigate(link)
      },
      spacing: 'sm'
    });
  }, [inventory_source_stock_num, source_name, source_sales_id, stock_num, navigate]);

  return (
    <StockNumberComponent
      name="stock_num"
      label="Stock #"
      readOnly={isTrade}
      required={!isTrade}
      append={
        isTrade &&
        isMobile && {
          use: 'edit',
          className: styles.button,
          onClick: handleEditStockNumber
        }
      }
      input={
        isTrade &&
        !isMobile && {
          append: (
            <Button className="w-auto" label="Edit" icon="edit" variant="secondary" onClick={handleEditStockNumber} />
          )
        }
      }
      inputGroup={
        isTrade &&
        !isMobile && {
          className: 'me-3'
        }
      }
    />
  );
};

// EXPORT
export default StockNumber;
